import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, FormControl, FormErrorMessage, HStack, Spinner, Text, Textarea, VStack, useColorModeValue, useToast } from '@chakra-ui/react';
import { useCreateAIChatSession, useSendAIChatMessageStream } from '@/lib/sdk/ai-chat/queries';
import { extractAIError, isChatEndedError } from '@/lib/sdk/ai-chat/types';
import { linkCitations } from '../citations';
import { GuideMarkdown } from './GuideMarkdown';

const askSchema = z.object({
  question: z.string().trim().min(1, 'Type a question').max(2000, 'Keep the question under 2000 characters'),
});

type AskFormData = z.infer<typeof askSchema>;

interface Exchange {
  role: 'user' | 'assistant';
  content: string;
  notCovered?: boolean;
}

interface AskPanelProps {
  titles: Record<string, string>;
  onOpenEntry: (entryId: string) => void;
}

/**
 * Ask mode: questions answered from the guide, with each answer's citations linking to
 * the entries it relies on. The session is created on the first question.
 */
export function AskPanel({ titles, onOpenEntry }: AskPanelProps) {
  const toast = useToast();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const createSession = useCreateAIChatSession();
  const sendMessage = useSendAIChatMessageStream();
  const userBg = useColorModeValue('brand.50', 'gray.700');
  const isAnswering = createSession.isPending || sendMessage.isPending;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<AskFormData>({ resolver: zodResolver(askSchema), defaultValues: { question: '' } });

  const onSubmit = async ({ question }: AskFormData) => {
    setExchanges((previous) => [...previous, { role: 'user', content: question }]);
    reset();
    try {
      const activeSessionId = sessionId ?? (await createSession.mutateAsync({ session_type: 'guide' })).id;
      setSessionId(activeSessionId);
      const response = await sendMessage.mutateAsync({
        sessionId: activeSessionId,
        data: { content: question },
        handlers: { onProgress: setProgress },
      });
      const answer = response.message;
      setExchanges((previous) => [
        ...previous,
        { role: 'assistant', content: answer?.content ?? '', notCovered: answer?.metadata?.not_covered ?? false },
      ]);
    } catch (error) {
      // Back in the box, ready to send again
      if (isChatEndedError(error)) {
        setSessionId(null);
        setExchanges([]);
      } else {
        setExchanges((previous) => previous.slice(0, -1));
      }
      setValue('question', question);
      const aiError = extractAIError(error);
      toast({
        status: 'error',
        title: 'The question could not be answered',
        description: aiError?.message ?? (error instanceof Error ? error.message : undefined),
      });
    } finally {
      setProgress(null);
    }
  };

  return (
    <VStack align="stretch" spacing={3} data-testid="guide-ask-panel">
      <Box>
        <Text fontWeight="semibold">Ask the guide</Text>
        <Text fontSize="xs" color="text-secondary">
          Answers come only from the guide and link to the entries they use.
        </Text>
      </Box>

      <VStack align="stretch" spacing={2} maxH="60vh" overflowY="auto">
        {exchanges.map((exchange, index) =>
          exchange.role === 'user' ? (
            <Box key={index} bg={userBg} borderRadius="md" px={3} py={2} alignSelf="flex-end" data-testid="guide-ask-question">
              <Text fontSize="sm">{exchange.content}</Text>
            </Box>
          ) : (
            <Box
              key={index}
              borderWidth="1px"
              borderColor="border-default"
              borderRadius="md"
              px={3}
              py={2}
              data-testid={exchange.notCovered ? 'guide-ask-not-covered' : 'guide-ask-answer'}
            >
              <GuideMarkdown content={linkCitations(exchange.content, titles)} onOpenEntry={onOpenEntry} fontSize="sm" />
            </Box>
          )
        )}
        {isAnswering && (
          <HStack spacing={2} data-testid="guide-ask-progress">
            <Spinner size="xs" />
            <Text fontSize="xs" color="text-secondary">
              {progress ?? 'Thinking…'}
            </Text>
          </HStack>
        )}
      </VStack>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl isInvalid={!!errors.question}>
          <Textarea
            size="sm"
            rows={3}
            placeholder="How do I…"
            isDisabled={isAnswering}
            data-testid="guide-ask-input"
            {...register('question')}
          />
          <FormErrorMessage>{errors.question?.message}</FormErrorMessage>
        </FormControl>
        <Button type="submit" size="sm" mt={2} isLoading={isAnswering} data-testid="guide-ask-send">
          Ask
        </Button>
      </form>
    </VStack>
  );
}
